import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Divider,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PersonAdd,
  Save,
  Cancel,
  CloudUpload,
  Email,
  Phone,
  Business,
  Star,
  StarBorder,
  Visibility,
  VisibilityOff,
  Settings,
  WorkOutline,
  Close
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { API_URL } from '../../utils/config';

interface Office {
  id: number;
  city: string;
  state_province_region: string;
  address1: string;
  address2: string;
  postal_code: string;
  country_id: number;
}

interface Contact {
  id: number;
  name: string;
  email: string | null;
  mobile: string | null;
  office: string | null;
  title: string | null;
  credentials: string | null;
  headshot_url: string | null;
  headshot_icon_url: string | null;
  horwathhtl_url: string | null;
  office_id: number;
  office_city: string;
  office_state: string | null;
  is_market_rep: boolean;
  is_primary: boolean | null;
  show_in_marketcompass: boolean | null;
  show_email: boolean | null;
  show_phone: boolean | null;
  show_headshot: boolean | null;
  display_order: number | null;
}

interface Country {
  id: number;
  name: string;
  code: string;
}

interface MarketRepresentativesProps {
  marketId: number;
  marketName: string;
  onUpdate?: () => void;
}

const FALLBACK_IMAGE = '/images/horwath_mountain.jpg';

export const MarketRepresentatives: React.FC<MarketRepresentativesProps> = ({
  marketId,
  marketName,
  onUpdate
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);

  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [emailError, setEmailError] = useState<string>('');
  const [isCreatingContact, setIsCreatingContact] = useState(false);

  // Form state for creating new contact
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    mobile: '',
    office: '',
    title: '',
    credentials: '',
    horwathhtl_url: '',
    office_id: 0,
    is_market_rep: true,
    show_in_marketcompass: true,
    show_email: true,
    show_phone: true,
    show_headshot: false,
    is_primary: false,
    display_order: null as number | null
  });

  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [headshotIconFile, setHeadshotIconFile] = useState<File | null>(null);
  const [headshotPreview, setHeadshotPreview] = useState<string>('');
  const [headshotIconPreview, setHeadshotIconPreview] = useState<string>('');

  // Form state for editing existing contact settings
  const [editSettings, setEditSettings] = useState({
    show_in_marketcompass: true,
    show_email: true,
    show_phone: true,
    show_headshot: false,
    is_primary: false,
    display_order: null as number | null
  });

  // Form state for editing contact details
  const [editContactData, setEditContactData] = useState({
    mobile: '',
    office: '',
    title: '',
    credentials: '',
    horwathhtl_url: '',
    headshot_url: '',
    headshot_icon_url: ''
  });
  const [editHeadshotFile, setEditHeadshotFile] = useState<File | null>(null);
  const [editHeadshotIconFile, setEditHeadshotIconFile] = useState<File | null>(null);
  const [editHeadshotPreview, setEditHeadshotPreview] = useState<string>('');
  const [editHeadshotIconPreview, setEditHeadshotIconPreview] = useState<string>('');;

  // State for "Add Contact from Other Country" modal
  const [addFromOtherCountryDialogOpen, setAddFromOtherCountryDialogOpen] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number>(0);
  const [otherCountryContacts, setOtherCountryContacts] = useState<Contact[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingOtherCountryContacts, setLoadingOtherCountryContacts] = useState(false);
  const [addingContacts, setAddingContacts] = useState(false);

  useEffect(() => {
    fetchMarketContacts();
  }, [marketId]);

  const fetchMarketContacts = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/markets/${marketId}/contacts`);
      if (response && response.ok) {
        const result = await response.json();
        const contactsList = result.data.contacts || [];
        setContacts(contactsList);

        // Derive unique offices from contacts (since API no longer returns offices separately)
        const uniqueOffices = Array.from(
          new Map(
            contactsList.map((contact: Contact) => [
              contact.office_id,
              {
                id: contact.office_id,
                city: contact.office_city,
                state_province_region: contact.office_state,
                address1: '',
                address2: '',
                postal_code: '',
                country_id: 0
              }
            ])
          ).values()
        ) as Office[];
        setOffices(uniqueOffices);
      }
    } catch (error) {
      console.error('Error fetching market contacts:', error);
      showSnackbar('Failed to fetch market contacts', 'error');
    }
    setLoading(false);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setEditSettings({
      show_in_marketcompass: contact.show_in_marketcompass ?? true,
      show_email: contact.show_email ?? true,
      show_phone: contact.show_phone ?? true,
      show_headshot: contact.show_headshot ?? false,
      is_primary: contact.is_primary ?? false,
      display_order: contact.display_order
    });
    setEditDialogOpen(true);
  };

  const handleEditContactDetails = (contact: Contact) => {
    setSelectedContact(contact);
    setEditContactData({
      mobile: contact.mobile || '',
      office: contact.office || '',
      title: contact.title || '',
      credentials: contact.credentials || '',
      horwathhtl_url: contact.horwathhtl_url || '',
      headshot_url: contact.headshot_url || '',
      headshot_icon_url: contact.headshot_icon_url || ''
    });
    setEditHeadshotPreview('');
    setEditHeadshotIconPreview('');
    setEditHeadshotFile(null);
    setEditHeadshotIconFile(null);
    setEditContactDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedContact) return;

    try {
      const response = await api.post(`/api/marketcompass/admin/markets/${marketId}/representatives`, {
        horwath_contact_id: selectedContact.id,
        ...editSettings
      });

      if (response.data.success) {
        showSnackbar('Representative settings updated successfully', 'success');
        setEditDialogOpen(false);
        fetchMarketContacts();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating representative:', error);
      showSnackbar('Failed to update representative settings', 'error');
    }
  };

  const handleRemoveRepresentative = async (contactId: number) => {
    if (!window.confirm('Are you sure you want to remove this representative from the market?')) {
      return;
    }

    try {
      const response = await api.post(`/api/marketcompass/admin/markets/${marketId}/representatives`, {
        horwath_contact_id: contactId,
        remove: true
      });

      if (response.data.success) {
        showSnackbar('Representative removed successfully', 'success');
        fetchMarketContacts();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error removing representative:', error);
      showSnackbar('Failed to remove representative', 'error');
    }
  };

  const handleAddExistingContact = async (contact: Contact) => {
    try {
      const response = await api.post(`/api/marketcompass/admin/markets/${marketId}/representatives`, {
        horwath_contact_id: contact.id,
        show_in_marketcompass: true,
        show_email: true,
        show_phone: true,
        show_headshot: false,
        is_primary: false
      });

      if (response.data.success) {
        showSnackbar('Representative added successfully', 'success');
        fetchMarketContacts();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error adding representative:', error);
      showSnackbar('Failed to add representative', 'error');
    }
  };

  const handleFileToBase64 = (file: File): Promise<{ base64: string; extension: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64 = base64String.split(',')[1];
        const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        resolve({ base64, extension });
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleSaveContactDetails = async () => {
    if (!selectedContact) return;

    try {
      const requestBody: any = {
        ...editContactData
      };

      // Handle image uploads
      if (editHeadshotFile) {
        const { base64, extension } = await handleFileToBase64(editHeadshotFile);
        requestBody.headshot_image_base64 = base64;
        requestBody.headshot_image_extension = extension;
      }

      if (editHeadshotIconFile) {
        const { base64, extension } = await handleFileToBase64(editHeadshotIconFile);
        requestBody.headshot_icon_base64 = base64;
        requestBody.headshot_icon_extension = extension;
      }

      const response = await api.put(`/api/marketcompass/admin/horwath-contacts/${selectedContact.id}`, requestBody);

      if (response.data.success) {
        showSnackbar('Contact details updated successfully', 'success');
        setEditContactDialogOpen(false);
        fetchMarketContacts();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating contact details:', error);
      showSnackbar('Failed to update contact details', 'error');
    }
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional, so empty is valid
    if (!email.endsWith('@horwathhtl.com')) {
      setEmailError('Email must end with @horwathhtl.com');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (email: string) => {
    setNewContact({ ...newContact, email });
    if (email) {
      validateEmail(email);
    } else {
      setEmailError('');
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.name || !newContact.office_id) {
      showSnackbar('Name and Office are required', 'error');
      return;
    }

    if (newContact.email && !validateEmail(newContact.email)) {
      showSnackbar('Please provide a valid @horwathhtl.com email address', 'error');
      return;
    }

    setIsCreatingContact(true);

    try {
      const requestBody: any = {
        ...newContact,
        market_id: marketId
      };

      // Handle image uploads
      if (headshotFile) {
        const { base64, extension } = await handleFileToBase64(headshotFile);
        requestBody.headshot_image_base64 = base64;
        requestBody.headshot_image_extension = extension;
      }

      if (headshotIconFile) {
        const { base64, extension } = await handleFileToBase64(headshotIconFile);
        requestBody.headshot_icon_base64 = base64;
        requestBody.headshot_icon_extension = extension;
      }

      const response = await api.post('/api/marketcompass/admin/horwath-contacts', requestBody);

      if (response.data.success) {
        showSnackbar('Contact created successfully', 'success');
        setCreateDialogOpen(false);
        resetNewContactForm();
        fetchMarketContacts();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      showSnackbar('Failed to create contact', 'error');
    } finally {
      setIsCreatingContact(false);
    }
  };

  const resetNewContactForm = () => {
    setNewContact({
      name: '',
      email: '',
      mobile: '',
      office: '',
      title: '',
      credentials: '',
      horwathhtl_url: '',
      office_id: 0,
      is_market_rep: true,
      show_in_marketcompass: true,
      show_email: true,
      show_phone: true,
      show_headshot: false,
      is_primary: false,
      display_order: null
    });
    setHeadshotFile(null);
    setHeadshotIconFile(null);
    setHeadshotPreview('');
    setHeadshotIconPreview('');
    setEmailError('');
    setIsCreatingContact(false);
  };

  const handleHeadshotChange = (event: React.ChangeEvent<HTMLInputElement>, isIcon: boolean) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isIcon) {
        setHeadshotIconFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setHeadshotIconPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setHeadshotFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setHeadshotPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handlers for "Add Contact from Other Country" feature
  const handleOpenAddFromOtherCountryDialog = async () => {
    setAddFromOtherCountryDialogOpen(true);
    setSelectedCountryId(0);
    setOtherCountryContacts([]);
    setSelectedContactIds([]);

    // Fetch all countries
    setLoadingCountries(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/store/markets/countries`);
      if (response && response.ok) {
        const result = await response.json();
        // Sort countries alphabetically by name
        const sortedCountries = result.data.sort((a: Country, b: Country) =>
          a.name.localeCompare(b.name)
        );
        setCountries(sortedCountries);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      showSnackbar('Failed to fetch countries', 'error');
    }
    setLoadingCountries(false);
  };

  const handleCountryChange = async (countryId: number) => {
    setSelectedCountryId(countryId);
    setOtherCountryContacts([]);
    setSelectedContactIds([]);

    if (countryId === 0) return;

    // Fetch contacts for the selected country
    setLoadingOtherCountryContacts(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/contacts/country/${countryId}`);
      if (response && response.ok) {
        const result = await response.json();
        // De-duplicate contacts by ID (in case a contact is associated with multiple offices)
        const uniqueContacts = result.data.contacts.reduce((acc: Contact[], current: Contact) => {
          const exists = acc.find(item => item.id === current.id);
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, [] as Contact[]);
        setOtherCountryContacts(uniqueContacts);
      }
    } catch (error) {
      console.error('Error fetching contacts by country:', error);
      showSnackbar('Failed to fetch contacts', 'error');
    }
    setLoadingOtherCountryContacts(false);
  };

  const handleToggleContactSelection = (contactId: number) => {
    setSelectedContactIds(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleAddSelectedContacts = async () => {
    if (selectedContactIds.length === 0) {
      showSnackbar('Please select at least one contact', 'error');
      return;
    }

    setAddingContacts(true);

    try {
      // Add each selected contact to the market
      const promises = selectedContactIds.map(contactId =>
        api.post(`/api/marketcompass/admin/markets/${marketId}/representatives`, {
          horwath_contact_id: contactId,
          show_in_marketcompass: true,
          show_email: true,
          show_phone: true,
          show_headshot: false,
          is_primary: false
        })
      );

      await Promise.all(promises);

      showSnackbar(`${selectedContactIds.length} contact(s) added successfully`, 'success');
      setAddFromOtherCountryDialogOpen(false);
      setSelectedContactIds([]);
      setOtherCountryContacts([]);
      setSelectedCountryId(0);
      fetchMarketContacts();
      onUpdate?.();
    } catch (error) {
      console.error('Error adding contacts:', error);
      showSnackbar('Failed to add contacts', 'error');
    } finally {
      setAddingContacts(false);
    }
  };

  // Filter and ensure distinct contacts by ID
  const uniqueContacts = contacts.reduce((acc, current) => {
    const exists = acc.find(item => item.id === current.id);
    if (!exists) {
      acc.push(current);
    }
    return acc;
  }, [] as Contact[]);

  const marketReps = uniqueContacts.filter(c => c.is_market_rep);
  const availableContacts = uniqueContacts.filter(c => !c.is_market_rep);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading representatives...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, backgroundColor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Market Representatives</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleOpenAddFromOtherCountryDialog}
            sx={{
              backgroundColor: 'transparent',
              color: '#003F9F',
              border: '2px solid #003F9F',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0, 63, 159, 0.04)',
                border: '2px solid #003F9F'
              }
            }}
          >
            Add Contact from Other Country
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              backgroundColor: '#32373C',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#1f2327' }
            }}
          >
            Create New Contact
          </Button>
        </Box>
      </Box>

      {/* Current Market Representatives */}
      <Typography variant="h6" sx={{ mb: 2 }}>Current Representatives</Typography>
      {marketReps.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {marketReps.map((contact) => (
            <Grid item xs={12} sm={6} md={4} key={contact.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit Contact Details">
                    <IconButton
                      size="small"
                      onClick={() => handleEditContactDetails(contact)}
                      color="primary"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Representative Settings">
                    <IconButton
                      size="small"
                      onClick={() => handleEditContact(contact)}
                      color="primary"
                    >
                      <Settings fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from Market">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveRepresentative(contact.id)}
                      color="error"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={contact.headshot_icon_url || contact.headshot_url || FALLBACK_IMAGE}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box sx={{ flex: 1, pr: 10 }}>
                      <Typography variant="h6" component="div">
                        {contact.name}
                      </Typography>
                      {contact.credentials && (
                        <Typography variant="caption" color="text.secondary">
                          {contact.credentials}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {contact.title && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {contact.title}
                    </Typography>
                  )}

                  <Stack spacing={0.5} sx={{ mb: 2 }}>
                    {contact.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="caption">{contact.email}</Typography>
                      </Box>
                    )}
                    {contact.mobile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="caption">{contact.mobile}</Typography>
                      </Box>
                    )}
                    {contact.office && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business fontSize="small" color="action" />
                        <Typography variant="caption">{contact.office}</Typography>
                      </Box>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {contact.is_primary && (
                      <Chip label="Primary" color="primary" size="small" icon={<Star />} />
                    )}
                    {contact.show_in_marketcompass && (
                      <Chip label="Public" color="success" size="small" />
                    )}
                    {contact.show_email && (
                      <Tooltip title="Email visible">
                        <Chip label="Email" size="small" icon={<Email fontSize="small" />} />
                      </Tooltip>
                    )}
                    {contact.show_phone && (
                      <Tooltip title="Phone visible">
                        <Chip label="Phone" size="small" icon={<Phone fontSize="small" />} />
                      </Tooltip>
                    )}
                    {contact.show_headshot && (
                      <Tooltip title="Headshot visible">
                        <Chip label="Photo" size="small" icon={<Visibility fontSize="small" />} />
                      </Tooltip>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert severity="info" sx={{ mb: 4 }}>
          No representatives assigned to this market yet.
        </Alert>
      )}

      {/* Available Contacts */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>
        Available Contacts from Offices in {marketName}'s Country
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Contacts associated with Horwath offices in the same country as this market
      </Typography>

      {availableContacts.length > 0 ? (
        <Grid container spacing={3}>
          {availableContacts.map((contact) => (
            <Grid item xs={12} sm={6} md={4} key={contact.id}>
              <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1, pb: 1 }}>
                  {/* Avatar and Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={contact.headshot_icon_url || contact.headshot_url || FALLBACK_IMAGE}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">{contact.name}</Typography>
                      {contact.credentials && (
                        <Typography variant="caption" color="text.secondary">
                          {contact.credentials}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Contact Information */}
                  <Stack spacing={1}>
                    {contact.title && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WorkOutline fontSize="small" color="action" />
                        {contact.title}
                      </Typography>
                    )}
                    {contact.email && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email fontSize="small" color="action" />
                        {contact.email}
                      </Typography>
                    )}
                    {contact.mobile && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone fontSize="small" color="action" />
                        {contact.mobile}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>

                {/* Add to Market Button - Bottom, Full Width */}
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleAddExistingContact(contact)}
                    sx={{
                      backgroundColor: '#32373C',
                      color: '#FFFFFF',
                      '&:hover': { backgroundColor: '#1f2327' }
                    }}
                  >
                    Add to Market
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert severity="info">
          No contacts found in Horwath offices located in this market's country. You can create a new contact using the "Create New Contact" button above, or use "Add Contact from Other Country" to add representatives from other regions.
        </Alert>
      )}

      {/* Edit Representative Settings Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Representative Settings</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedContact.name}
              </Typography>
              <FormGroup sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSettings.is_primary}
                      onChange={(e) => setEditSettings({ ...editSettings, is_primary: e.target.checked })}
                    />
                  }
                  label="Primary Representative"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSettings.show_in_marketcompass}
                      onChange={(e) => setEditSettings({ ...editSettings, show_in_marketcompass: e.target.checked })}
                    />
                  }
                  label="Show in MarketCompass"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSettings.show_email}
                      onChange={(e) => setEditSettings({ ...editSettings, show_email: e.target.checked })}
                    />
                  }
                  label="Show Email Address"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSettings.show_phone}
                      onChange={(e) => setEditSettings({ ...editSettings, show_phone: e.target.checked })}
                    />
                  }
                  label="Show Phone Number"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSettings.show_headshot}
                      onChange={(e) => setEditSettings({ ...editSettings, show_headshot: e.target.checked })}
                    />
                  }
                  label="Show Headshot Photo"
                />
              </FormGroup>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={editSettings.display_order || ''}
                onChange={(e) => setEditSettings({
                  ...editSettings,
                  display_order: e.target.value ? parseInt(e.target.value) : null
                })}
                sx={{ mt: 2 }}
                helperText="Lower numbers appear first"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Details Dialog */}
      <Dialog open={editContactDialogOpen} onClose={() => setEditContactDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Contact Details</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6">{selectedContact.name}</Typography>
                {selectedContact.credentials && (
                  <Typography variant="body2" color="text.secondary">
                    Current Credentials: {selectedContact.credentials}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={editContactData.mobile}
                  onChange={(e) => setEditContactData({ ...editContactData, mobile: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Office Phone"
                  value={editContactData.office}
                  onChange={(e) => setEditContactData({ ...editContactData, office: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editContactData.title}
                  onChange={(e) => setEditContactData({ ...editContactData, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Credentials"
                  value={editContactData.credentials}
                  onChange={(e) => setEditContactData({ ...editContactData, credentials: e.target.value })}
                  placeholder="e.g., MBA, CPA"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Horwath HTL Profile URL"
                  value={editContactData.horwathhtl_url}
                  onChange={(e) => setEditContactData({ ...editContactData, horwathhtl_url: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Photos</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Headshot URL"
                  value={editContactData.headshot_url}
                  onChange={(e) => setEditContactData({ ...editContactData, headshot_url: e.target.value })}
                  helperText="Direct URL or upload new image below"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Headshot Icon URL"
                  value={editContactData.headshot_icon_url}
                  onChange={(e) => setEditContactData({ ...editContactData, headshot_icon_url: e.target.value })}
                  helperText="Direct URL or upload new icon below"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    fullWidth
                  >
                    Upload New Headshot
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setEditHeadshotFile(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEditHeadshotPreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                  {editHeadshotPreview && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <img
                        src={editHeadshotPreview}
                        alt="Headshot preview"
                        style={{ maxWidth: '100%', maxHeight: 200 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    fullWidth
                  >
                    Upload New Icon (Small)
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setEditHeadshotIconFile(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEditHeadshotIconPreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                  {editHeadshotIconPreview && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <img
                        src={editHeadshotIconPreview}
                        alt="Icon preview"
                        style={{ maxWidth: '100%', maxHeight: 100 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContactDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveContactDetails} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Create New Contact Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Contact</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name *"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Office * (in {marketName}'s country)</InputLabel>
                <Select
                  value={newContact.office_id}
                  onChange={(e) => setNewContact({ ...newContact, office_id: e.target.value as number })}
                  label="Office * (in market's country)"
                >
                  <MenuItem value={0}>Select Office</MenuItem>
                  {offices.length > 0 ? (
                    offices.map((office) => (
                      <MenuItem key={office.id} value={office.id}>
                        {office.city}{office.state_province_region && `, ${office.state_province_region}`}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={0} disabled>
                      No offices found in this country
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newContact.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                error={!!emailError}
                helperText={emailError || 'Must end with @horwathhtl.com'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                value={newContact.mobile}
                onChange={(e) => setNewContact({ ...newContact, mobile: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Office Phone"
                value={newContact.office}
                onChange={(e) => setNewContact({ ...newContact, office: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={newContact.title}
                onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Credentials"
                value={newContact.credentials}
                onChange={(e) => setNewContact({ ...newContact, credentials: e.target.value })}
                placeholder="e.g., MBA, CPA"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Horwath HTL Profile URL"
                value={newContact.horwathhtl_url}
                onChange={(e) => setNewContact({ ...newContact, horwathhtl_url: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Photos</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  Upload Headshot
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleHeadshotChange(e, false)}
                  />
                </Button>
                {headshotPreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={headshotPreview}
                      alt="Headshot preview"
                      style={{ maxWidth: '100%', maxHeight: 200 }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  Upload Icon (Small)
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleHeadshotChange(e, true)}
                  />
                </Button>
                {headshotIconPreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={headshotIconPreview}
                      alt="Icon preview"
                      style={{ maxWidth: '100%', maxHeight: 100 }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Market Representative Settings</Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newContact.is_market_rep}
                      onChange={(e) => setNewContact({ ...newContact, is_market_rep: e.target.checked })}
                    />
                  }
                  label="Add as Market Representative"
                />
                {newContact.is_market_rep && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newContact.is_primary}
                          onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                        />
                      }
                      label="Primary Representative"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newContact.show_in_marketcompass}
                          onChange={(e) => setNewContact({ ...newContact, show_in_marketcompass: e.target.checked })}
                        />
                      }
                      label="Show in MarketCompass"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newContact.show_email}
                          onChange={(e) => setNewContact({ ...newContact, show_email: e.target.checked })}
                        />
                      }
                      label="Show Email Address"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newContact.show_phone}
                          onChange={(e) => setNewContact({ ...newContact, show_phone: e.target.checked })}
                        />
                      }
                      label="Show Phone Number"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newContact.show_headshot}
                          onChange={(e) => setNewContact({ ...newContact, show_headshot: e.target.checked })}
                        />
                      }
                      label="Show Headshot Photo"
                    />
                  </>
                )}
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetNewContactForm();
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateContact}
            variant="contained"
            disabled={!newContact.name || !newContact.office_id || !!emailError || isCreatingContact}
          >
            {isCreatingContact ? 'Creating...' : 'Create Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contact from Other Country Dialog */}
      <Dialog
        open={addFromOtherCountryDialogOpen}
        onClose={() => setAddFromOtherCountryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Contact from Other Country</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Country Selection */}
            <Autocomplete
              fullWidth
              options={countries}
              getOptionLabel={(option) => option.name}
              value={countries.find(c => c.id === selectedCountryId) || null}
              onChange={(event, newValue) => {
                handleCountryChange(newValue?.id || 0);
              }}
              loading={loadingCountries}
              disabled={loadingCountries}
              sx={{ mb: 3 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Country"
                  placeholder="Type to search..."
                />
              )}
            />

            {/* Loading indicator for contacts */}
            {loadingOtherCountryContacts && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>Loading contacts...</Typography>
              </Box>
            )}

            {/* Contacts List */}
            {!loadingOtherCountryContacts && otherCountryContacts.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Select Contacts to Add ({selectedContactIds.length} selected)
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {otherCountryContacts.map((contact) => (
                    <Card
                      key={contact.id}
                      sx={{
                        m: 1,
                        cursor: 'pointer',
                        backgroundColor: selectedContactIds.includes(contact.id) ? 'rgba(0, 63, 159, 0.08)' : 'white',
                        border: selectedContactIds.includes(contact.id) ? '2px solid #003F9F' : '1px solid #e0e0e0',
                        '&:hover': {
                          backgroundColor: selectedContactIds.includes(contact.id) ? 'rgba(0, 63, 159, 0.12)' : 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                      onClick={() => handleToggleContactSelection(contact.id)}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                        <Checkbox
                          checked={selectedContactIds.includes(contact.id)}
                          onChange={() => handleToggleContactSelection(contact.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Avatar
                          src={contact.headshot_icon_url || contact.headshot_url || FALLBACK_IMAGE}
                          sx={{ width: 50, height: 50, mx: 2 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1">
                            {contact.name}
                            {contact.credentials && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {contact.credentials}
                              </Typography>
                            )}
                          </Typography>
                          {contact.title && (
                            <Typography variant="body2" color="text.secondary">
                              {contact.title}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            {contact.email && (
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Email fontSize="small" />
                                {contact.email}
                              </Typography>
                            )}
                            {contact.office_city && (
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Business fontSize="small" />
                                {contact.office_city}{contact.office_state && `, ${contact.office_state}`}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </>
            )}

            {/* No contacts message */}
            {!loadingOtherCountryContacts && selectedCountryId !== 0 && otherCountryContacts.length === 0 && (
              <Alert severity="info">
                No contacts found in the selected country.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFromOtherCountryDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedContacts}
            variant="contained"
            disabled={selectedContactIds.length === 0 || addingContacts}
            sx={{
              backgroundColor: '#32373C',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#1f2327' }
            }}
          >
            {addingContacts ? 'Adding...' : `Add ${selectedContactIds.length} Contact(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};